import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { CertificationService } from '../../../core/services/certification.service';
import {
  Certification,
  CERTIFICATION_TYPE_NAMES,
  getCertificationStatusClass,
  getCertificationStatusText,
  getCertificationStatusIcon
} from '../../../core/models/certification.model';
import { CertificationUploadModalComponent } from '../certification-upload-modal.component';
import { formatCalendarDate } from '../../utils/date.utils';
import { environment } from '../../../../environments/environment';

/**
 * Certifications Card Component
 *
 * Displays and manages certifications for either an organization or a user profile.
 * Consumers must pass exactly one of `organizationId` or `userId`.
 */
@Component({
  selector: 'app-certifications-card',
  standalone: true,
  imports: [CommonModule, CertificationUploadModalComponent],
  templateUrl: './certifications-card.component.html',
  styleUrls: ['./certifications-card.component.scss']
})
export class CertificationsCardComponent implements OnInit, OnDestroy {
  @Input() organizationId?: string;
  @Input() userId?: string;
  @Input() small?: boolean;

  certifications: Certification[] = [];
  loading = true;
  errorMessage = '';
  showUploadModal = false;
  editingCertification?: Certification;

  private destroy$ = new Subject<void>();

  constructor(private certificationService: CertificationService) {}

  ngOnInit(): void {
    const ownerCount = [this.organizationId, this.userId].filter(Boolean).length;
    if (ownerCount !== 1) {
      this.errorMessage = 'Either organizationId or userId must be provided (exactly one).';
      this.loading = false;
      return;
    }

    this.loadCertifications();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCertifications(): void {
    this.loading = true;
    this.errorMessage = '';

    const source$ = this.organizationId
      ? this.certificationService.getOrganizationCertifications(this.organizationId)
      : this.certificationService.getMemberCertifications(this.userId!);

    source$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (certifications) => {
        this.certifications = certifications;
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load certifications:', error);
        this.errorMessage = 'Failed to load certifications. Please try again.';
        this.loading = false;
      }
    });
  }

  openUploadModal(): void {
    this.editingCertification = undefined;
    this.showUploadModal = true;
  }

  editCertification(certification: Certification): void {
    this.editingCertification = certification;
    this.showUploadModal = true;
  }

  closeUploadModal(): void {
    this.showUploadModal = false;
    this.editingCertification = undefined;
  }

  onCertificationUploaded(): void {
    this.showUploadModal = false;
    this.editingCertification = undefined;
    this.loadCertifications();
  }

  deleteCertification(certification: Certification): void {
    if (!confirm(`Are you sure you want to delete this ${this.getCertificationTypeName(certification.certificationType)} certification?`)) {
      return;
    }

    this.certificationService
      .deleteCertification(certification.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadCertifications();
        },
        error: (error) => {
          console.error('Failed to delete certification:', error);
          alert('Failed to delete certification. Please try again.');
        }
      });
  }

  viewCertificate(certification: Certification): void {
    if (certification.documentUrl) {
      this.certificationService.getDownloadUrl(certification.id).subscribe({
        next: (downloadUrl) => {
          const fullUrl = this.getCertificationFullUrl(downloadUrl);
          window.open(fullUrl, '_blank');
        },
        error: (error) => {
          console.error('Failed to get download URL:', error);
          this.errorMessage = 'Failed to open document. Please try again.';
        }
      });
    }
  }

  getCertificationTypeName(type: string): string {
    return CERTIFICATION_TYPE_NAMES[type as keyof typeof CERTIFICATION_TYPE_NAMES] || type;
  }

  getStatusClass(certification: Certification): string {
    return getCertificationStatusClass(certification.status, certification.expirationDate);
  }

  getStatusText(certification: Certification): string {
    return getCertificationStatusText(certification.status, certification.expirationDate);
  }

  getStatusIcon(certification: Certification): string {
    return getCertificationStatusIcon(certification.status, certification.expirationDate);
  }

  formatDate(date: string | null): string {
    return formatCalendarDate(date);
  }

  getFileCount(certification: Certification): number {
    return certification.documentUrl ? 1 : 0;
  }

  getCertificationFullUrl(certUrl: string | null | undefined): string {
    if (!certUrl) return '';
    if (certUrl.startsWith('http')) {
      const signed = new URL(certUrl);
      signed.host = new URL(environment.appCDN).host;
      return signed.toString();
    }
    return `${environment.appCDN}/${certUrl}`;
  }
}
